// Example request body for Discover Images v2 taken from:
// https://code.amazon.com/packages/AlexaMultimediaDiscoveryService/blobs/mainline/--/data/v2/discover/images/request-with-query-and-entities.json
const requestBodyTemplate = {
    "query_text": "obama in a late night show",
    "response_dimensions": {
        "size": "5"
    },
    "request_metadata": {
        "profile": "WBR_AGILT"
    }
};

// MMR video profiles: https://w.amazon.com/bin/view/MMIR/MultimodalRetrievalService/RequestProfiles
// Each maps to the AMD ProfileType that the MMRS shim resolves back to that MMR profile.
// VIDEO_ALL is omitted until the shim mapping adds an AMD ProfileType for it.
export const VIDEO_PROFILES = ['VIDEO_CORE', 'VIDEO_AECS', 'VIDEO_YT', 'VIDEO_PV'];

const MMR_TO_AMD_PROFILE = {
    'VIDEO_CORE': 'WBR_AGILT',
    'VIDEO_AECS': 'Banyan',
    'VIDEO_YT': 'Rufus',
    'VIDEO_PV': 'PrimeVideo',
};

export const DEFAULT_VIDEO_PROFILE = 'VIDEO_CORE';

// Image profiles map directly to the AMD request_metadata.profile the service accepts
// (no MMR->AMD shim indirection is needed for images).
export const IMAGE_PROFILES = ['WBR_AGILT', 'Banyan', 'Classic', 'Metis'];
export const DEFAULT_IMAGE_PROFILE = 'WBR_AGILT';

// Sentinel meaning "omit request_metadata.locale". Follows the same convention as the other
// Tools dropdowns, whose first option ('All', 'Any time') means "no constraint".
export const NO_IMAGE_LOCALE = 'Not sent';

// Locales offered by the Images Tools bar, feeding request_metadata.locale.
// Kept in step with src/components/filters/VisualiserLocaleSelect.js so the two
// locale controls in this app do not drift apart.
export const IMAGE_LOCALES = [
    NO_IMAGE_LOCALE,
    'en-US', 'en-GB', 'de-DE', 'en-CA', 'en-AU', 'fr-FR', 'fr-CA', 'it-IT',
    'es-ES', 'es-US', 'en-IN', 'ja-JP', 'es-MX', 'hi-IN', 'pt-BR', 'ar-SA',
];

// Nothing is sent unless a locale is chosen. locale is optional on the request, and defaulting
// it to a real value biased every search towards en-US retrieval whether or not the user had
// thought about locale. The template no longer carries it either, so the video path (which never
// set locale itself) stops inheriting en-US too.
export const DEFAULT_IMAGE_LOCALE = NO_IMAGE_LOCALE;

export const exampleDiscoverImagesV2RequestBody = JSON.stringify(requestBodyTemplate, null, 2);
export const getDiscoverImagesV2RequestBody = (query_text, result_size = "100", enableQueryIntelligence = false, profile = DEFAULT_IMAGE_PROFILE, locale = DEFAULT_IMAGE_LOCALE) => {
    let requestBody = JSON.parse(JSON.stringify(requestBodyTemplate));
    requestBody.response_dimensions.size = result_size;
    requestBody.query_text = query_text;
    requestBody.request_metadata.profile = profile;
    if (locale && locale !== NO_IMAGE_LOCALE) {
        requestBody.request_metadata.locale = locale;
    }

    if (enableQueryIntelligence) {
        requestBody.custom_config = {
            "query_intelligence_config": {
                "enable_query_intelligence": true
            }
        };
    }

    return JSON.stringify(requestBody, null, 2);
}

const buildVideoRequestBody = (query_text, mmrProfile, result_size, grounded_answer) => {
    const amdProfile = MMR_TO_AMD_PROFILE[mmrProfile] || MMR_TO_AMD_PROFILE[DEFAULT_VIDEO_PROFILE];
    let requestBody = JSON.parse(JSON.stringify(requestBodyTemplate));
    requestBody.response_dimensions.size = result_size;
    requestBody.query_text = grounded_answer || query_text;
    requestBody.request_metadata.profile = amdProfile;
    requestBody.modality = 'VIDEO';
    requestBody.custom_config = {
        "ltr_threshold": "0",
        "disable_sensitivity_check": true
    };
    if (grounded_answer) {
        requestBody.query_metadata = {
            "grounding_query": query_text,
            "answer": grounded_answer
        };
    }
    return JSON.stringify(requestBody, null, 2);
};

export const getDiscoverVideosRequestBody = (query_text, profile = DEFAULT_VIDEO_PROFILE, result_size = "20") => {
    return buildVideoRequestBody(query_text, profile, result_size);
}

export const getDiscoverImagesV2RequestWithGroundingBody = (query_text, grounded_answer, result_size = "100", enableQueryIntelligence = false, profile = DEFAULT_IMAGE_PROFILE, locale = DEFAULT_IMAGE_LOCALE) => {
    let requestBody = JSON.parse(JSON.stringify(requestBodyTemplate));
    requestBody.response_dimensions.size = result_size;
    requestBody.query_text = grounded_answer;
    requestBody.request_metadata.profile = profile;
    if (locale && locale !== NO_IMAGE_LOCALE) {
        requestBody.request_metadata.locale = locale;
    }
    requestBody.query_metadata = {
        "grounding_query": query_text,
        "answer": grounded_answer
    }

    if (enableQueryIntelligence) {
        requestBody.custom_config = {
            "query_intelligence_config": {
                "enable_query_intelligence": true
            }
        };
    }

    return JSON.stringify(requestBody, null, 2);
}

export const getDiscoverVideosRequestWithGroundingBody = (query_text, grounded_answer, profile = DEFAULT_VIDEO_PROFILE, result_size = "20") => {
    return buildVideoRequestBody(query_text, profile, result_size, grounded_answer);
}
